import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["frame", "title"];
  static values = {
    openOnConnect: Boolean,
    portal: { type: Boolean, default: true }
  };
  static supportsNativeExitAnimation = CSS.supports("overlay", "auto") && CSS.supports("transition-behavior", "allow-discrete");

  get closedBy() {
    return this.element.getAttribute("closedby") || "closerequest";
  }

  get allowsEscapeClose() {
    return this.closedBy === "any" || this.closedBy === "closerequest";
  }

  get allowsBackdropClose() {
    return this.closedBy === "any";
  }

  initialize() {
    if (this.portalValue) this.portal();
  }

  connect() {
    this.bindEvents();
    if (this.openOnConnectValue) this.show();
  }

  disconnect() {
    cancelAnimationFrame(this.focusRequest);
    this.closingAnimation = null;
    this.element.removeEventListener("transitionend", this.didFinishOpening);
    this.element.removeEventListener("close", this.didClose);
    this.element.removeEventListener("cancel", this.didCancel);
    this.element.removeEventListener("click", this.didClick);
    this.element.removeEventListener("command", this.didReceiveCommand);
    this.element.removeEventListener("turbo:before-fetch-request", this.captureFrameContent);
    document.removeEventListener("turbo:before-cache", this.closeBeforeCache);
  }
  portal() {
    if (this.element.parentElement !== document.body) {
      document.body.appendChild(this.element);
    }
  }

  bindEvents() {
    this.element.addEventListener("transitionend", this.didFinishOpening);
    this.element.addEventListener("close", this.didClose);
    this.element.addEventListener("cancel", this.didCancel);
    this.element.addEventListener("command", this.didReceiveCommand);
    this.element.addEventListener("turbo:before-fetch-request", this.captureFrameContent);
    document.addEventListener("turbo:before-cache", this.closeBeforeCache);

    this.element.addEventListener("click", this.didClick);
  }
  didClose = () => {
    this.closingAnimation = null;
    this.element.classList.remove("closing");
    this.resetLazyFrames();
  };
  closeBeforeCache = () => {
    if (this.openOnConnectValue) this.openOnConnectValue = false;
    if (this.element.open) {
      this.element.classList.remove("closing");
      this.element.close();
    }
  };

  didFinishOpening = (event) => {
    if (event.target !== this.element) return;
    if (!this.element.open) return;
    if (event.propertyName !== "opacity") return;

    this.manageFocus();
  };

  didCancel = (event) => {
    event.preventDefault();
    if (this.allowsEscapeClose) this.animateClose();
  };
  didReceiveCommand = (event) => {
    if (event.command !== "show-modal") return;

    this.closeOtherModals();
    this.loadLazyFrames();
    this.scheduleFocus();
  };

  captureFrameContent = (event) => {
    const frame = event.target.closest("turbo-frame");
    if (frame && frame.dataset.originalHtml === undefined) {
      frame.dataset.originalHtml = frame.innerHTML;
    }
  };

  didClick = (event) => {
    if (event.target === this.element) {
      const bounds = this.element.getBoundingClientRect();
      const outside = event.clientX < bounds.left || event.clientX > bounds.right ||
        event.clientY < bounds.top || event.clientY > bounds.bottom;
      if (outside && this.allowsBackdropClose) this.close();
      return;
    }

    if (this.constructor.supportsNativeExitAnimation) return;

    const button = event.target.closest("button, input[type='submit']");
    if (!button) return;

    const command = button.getAttribute("command");
    const formmethod = button.getAttribute("formmethod");

    if (command === "close") {
      event.preventDefault();
      this.animateClose();
    } else if (formmethod === "dialog") {
      event.preventDefault();
      this.animateClose(button.value);
    }
  };
  show() {
    if (this.element.open) return;

    this.closeOtherModals();
    this.loadLazyFrames();
    this.element.showModal();
    this.scheduleFocus();
  }

  scheduleFocus() {
    cancelAnimationFrame(this.focusRequest);
    this.focusRequest = requestAnimationFrame(() => {
      if (this.element.open && window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
        this.manageFocus();
      }
    });
  }

  preventCloseOnMorphing(event) {
    if (this.element.open && event.detail?.attributeName === "open") event.preventDefault();
  }

  close() {
    if (this.constructor.supportsNativeExitAnimation) {
      this.element.close();
    } else {
      this.animateClose();
    }
  }
  submitEnd(event) {
    if (event.detail.success) this.close();
  }
  dismiss() {
    this.close();
  }

  setFrameSrc(src) {
    if (this.hasFrameTarget) this.frameTarget.src = src;
  }

  setTitle(text) {
    if (this.hasTitleTarget) this.titleTarget.textContent = text;
  }

  loadLazyFrames() {
    this.element.querySelectorAll("turbo-frame[loading='lazy']").forEach(frame => {
      frame.loading = "eager";
    });
  }
  closeOtherModals() {
    document.querySelectorAll("dialog:modal").forEach(dialog => {
      if (dialog !== this.element) dialog.close();
    });
  }

  animateClose(returnValue) {
    if (this.element.classList.contains("closing")) return;
    if (!this.element.open) return;

    this.element.classList.add("closing");

    const animation = {};
    this.closingAnimation = animation;
    const cleanup = () => {
      if (this.closingAnimation !== animation) return;
      this.element.close(returnValue);
      this.element.classList.remove("closing");
    };
    const animations = this.element.getAnimations();

    if (animations.length === 0) {
      cleanup();
    } else {
      Promise.all(animations.map(animation => animation.finished)).then(cleanup, cleanup);
    }
  }

  manageFocus() {
    const variant = this.element.dataset.mobile;
    const shouldBlur = (variant === 'sheet' || variant === 'action') && window.innerWidth < 768;
    const autofocused = shouldBlur ? null : this.element.querySelector("[autofocus]");
    if (autofocused) {
      autofocused.focus();
    } else if (shouldBlur && this.element.contains(document.activeElement) &&
      document.activeElement.matches("input, textarea, [contenteditable]")) {
      document.activeElement.blur();
    }
  }

  resetLazyFrames() {
    this.element.querySelectorAll("turbo-frame[data-original-html]").forEach(frame => {
      frame.innerHTML = frame.dataset.originalHtml;
      frame.removeAttribute("complete");
      frame.loading = "lazy";
    });
  }
}
